<template>
  <div :class="[classes.tabWrapper, layoutClass, 'tags']">
    <div v-for="tag in tags" :key="tag" :class="[classes.tag, layoutClass]">
      <DynamicText
        v-if="!!subText || tag.length <= 10"
        tag="p"
        :class="[classes.mainText, 'mainText']"
        styleGroup="Event Tag"
        :get="mainTextStyle.get"
        :set="mainTextStyle.set"
      >
        {{ tag }}
      </DynamicText>
      <TagMarquee
        v-else
        :class="[classes.mainText, 'mainText']"
        tag="p"
        styleGroup="Event Tag"
        :get="mainTextStyle.get"
        :set="mainTextStyle.set"
      >
        {{ tag }}
      </TagMarquee>
      <DynamicText
        v-if="subText"
        :id="subText"
        tag="p"
        :class="[classes.subText, 'subText']"
        styleGroup="Event Tag Subtext"
        :get="subTextStyle.get"
        :set="subTextStyle.set"
      >
        {{ subText }}
      </DynamicText>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { Brand } from "@fresh8/types/src";
import DynamicText from "./DynamicText.vue";
import TagMarquee from "./TagMarquee.vue";
import { injectCarouselContext } from "../lib/provideInjectors";
import {
  injectTemplateSize,
  LARGE_MPU,
  SKYSCRAPER
} from "@fresh8/ad-data-composition/src/useTemplateSize";

const useStyles = createUseStyles({
  tabWrapper: {
    minHeight: "28px",
    display: "flex",
    flexDirection: "row",
    justifyContent: props =>
      props.multiTags ? "space-between" : "space-around",
    padding: "0px 8px 16px",

    [`&.${SKYSCRAPER.name}`]: {
      minHeight: "19px",
      width: props => (props.multiTags ? "" : "120px"),
      margin: props => (props.multiTags ? "" : "auto"),
      padding: props => (props.multiTags ? "0px 5px 16px " : "0px 0px 16px")
    }
  },
  tag: {
    borderRight: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    borderBottom: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    borderLeft: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "0px 0px 10px 10px",
    padding: props => (props.multiTags ? "" : "0px 12px"),

    [`&.${SKYSCRAPER.name}`]: {
      minWidth: props => (props.isCarousel ? "54px" : "60px"),
      fontSize: "12px"
    },

    [`&.${LARGE_MPU.name}`]: {
      minWidth: props => (props.isCarousel ? "64px" : "70px")
    }
  },
  mainText: {
    fontSize: props => (props.hasSubText ? "10px" : "14px"),
    lineHeight: props => (props.hasSubText ? "12px" : "")
  },
  subText: {
    fontSize: "8px"
  }
});

export default defineComponent({
  name: "MarketTags",
  components: {
    DynamicText,
    TagMarquee
  },
  props: {
    tags: {
      type: Array as PropType<string[]>,
      required: true
    },
    subText: {
      type: String,
      required: false,
      default: ""
    }
  },
  setup(props) {
    const brandContext = injectBrand();
    const layout = injectTemplateSize();
    const carouselContext = injectCarouselContext();

    const relativeStyles = computed(() => ({
      ...(brandContext?.adTemplateBranding.value || {}),
      multiTags: props.tags.length > 1,
      isCarousel: carouselContext.value.isCarousel,
      hasSubText: !!props.subText
    }));

    const classes = useStyles(relativeStyles);

    return {
      classes,
      layoutClass: computed(() => layout?.size.value.name),
      mainTextStyle: {
        get: (brand: Brand | undefined) =>
          (brand && brand.adTemplate.eventTag?.mainTextFont) ||
          (brand && brand.adTemplate.market.teamNameFont),
        set: (brand: Brand | undefined, value: string) => {
          if (brand) {
            brand.adTemplate.eventTag.mainTextFont = value;
          }
        }
      },
      subTextStyle: {
        get: (brand: Brand | undefined) =>
          (brand && brand.adTemplate.eventTag?.subTextFont) ||
          (brand && brand.adTemplate.market.teamNameFont),
        set: (brand: Brand, value: string) => {
          brand.adTemplate.eventTag.subTextFont = value;
        }
      }
    };
  }
});
</script>
